import { Controller } from "stimulus";

const ALLOWED_TYPES = [
    "audio/mp3",
    "audio/mpeg",
    "audio/wav",
    "video/mp4",
    "video/quicktime"
];

const AUDIO_TYPES = [
    "audio/mp3",
    "audio/mpeg",
    "audio/wav",
];

const VIDEO_TYPES = [
    "video/mp4",
    "video/quicktime",
];

export default class UploadController extends Controller {
    connect() {
        this.uploading = [];
        this._uploadStats = {};

        this.uppy = new window.Uppy.Uppy(this.uppyConfig());
        this.attachTus(this.uppy);

        this.extracting = false;
        if (this.hasEpisodeIdValue && this.episodeIdValue) {
            this.checkExtractionStatusAndMaybePoll();
        }
    }

    dropZoneTargetConnected(el) {
        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        let icon = document.getElementById("cloud");

        el.addEventListener("dragover", prevent);
        el.addEventListener("dragenter", (e) => {
            prevent(e);
            icon.classList.add("text-blue-700");
            icon.classList.remove("text-gray-500");
        });
        el.addEventListener("dragleave", (e) => {
            prevent(e);
            icon.classList.add("text-gray-500");
            icon.classList.remove("text-blue-700");
        });

        el.addEventListener("drop", (e) => {
            e.preventDefault();

            const file = e.dataTransfer.files[0];

            let replace;
            if (VIDEO_TYPES.includes(file.type)) {
                replace = this.getFileByType(VIDEO_TYPES);
            } else {
                replace = this.getFileByType(AUDIO_TYPES);
            }
            if (replace) this.uppy.removeFile(replace.id);
            this.uppy.addFile(file);
        });
    }

    eventTargetConnected(el) {
        el.addEventListener("upload-started", (e) => {
            const { episodeId } = e.detail;
            if (episodeId === null) return;

            window.Turbo.visit(`/dashboard/channels/episodes/${episodeId}/detail`, { frame: "step" });
        });
    }

    previewVideoTargetConnected(el) {
        if (this.hasVideoImageInputTarget) {
            this.videoImageInputTarget.addEventListener("change", () => {
                if (FileReader && this.videoImageInputTarget.files && this.videoImageInputTarget.files.length) {
                    var fr = new FileReader();
                    fr.onload = function () {
                        el.poster = fr.result;
                        el.load(); // Reset the video so the new thumbnail displays
                    }
                    fr.readAsDataURL(this.videoImageInputTarget.files[0]);
                }
            });
        }
        
        const video = this.getFileByType(VIDEO_TYPES);
        if (!video || !this.hasPreviewVideoTarget) return;

        this.updateVideoPreview(video); 
    }

    previewAudioTargetConnected(el) {
        if (this.hasAudioImageInputTarget) {
            this.audioImageInputTarget.addEventListener("change", () => {
                if (FileReader && this.audioImageInputTarget.files && this.audioImageInputTarget.files.length) {
                    var fr = new FileReader();
                    fr.onload = function () {
                        document.getElementById("preview-image").src = fr.result;
                    }
                    fr.readAsDataURL(this.audioImageInputTarget.files[0]);
                }
            });
        }

        const audio = this.getFileByType(["audio/mp3", "audio/mpeg", "audio/wav"]);
        if (!audio || !this.hasPreviewAudioTarget) return;

        this.updateAudioPreview(audio);
    }

    extractionNoticeTargetConnected(el) {
        this.toggleExtractionNotice();
    }

    videoOnlyTargetConnected(el) {
        const video = this.getFileByType(VIDEO_TYPES);
        if (!video) return;
        
        el.classList.remove("hidden");
    }

    audioOnlyTargetConnected(el) {
        const audio = this.getFileByType(AUDIO_TYPES);
        if (!audio) return;

        el.classList.remove("hidden");
    }

    toggleExtractionNotice() {
        if (this.hasExtractionNoticeTarget) {
            if (this.extracting) {
                this.extractionNoticeTarget.classList.remove('hidden');
                this.previewAudioTarget.parentElement.classList.add('hidden');
            } else {
                this.extractionNoticeTarget.classList.add('hidden');
            }
        }
    }

    updateAudioPreview(audio) {
        if (this.hasPreviewAudioTarget) {
            const audioEl = this.previewAudioTarget.querySelector("audio");
            audioEl.src = URL.createObjectURL(audio.data);
            this.previewAudioTarget.parentElement.classList.remove("hidden");
        }
    }

    updateVideoPreview(video) {
        if (this.hasPreviewVideoTarget) {
            const current = this.previewVideoTarget.querySelector("source");
            const source = document.createElement("source");
            source.src = URL.createObjectURL(video.data);
            source.type = video.type;
            if (current) {
                this.previewVideoTarget.removeChild(current);
            }
            this.previewVideoTarget.appendChild(source);
            this.previewVideoTarget.load(); // Force a reload after changing
            this.previewVideoTarget.parentElement.classList.remove("hidden");
        }
    }

    getFileByType(fileTypes) {
        const files = this.uppy.getFiles();
        for (const file of files) {
            if (fileTypes.includes(file.type)) return file;
        }
        return null;
    }

    logUploadState(upload_log_id, state, error_message = null) {
        const csrfToken = document.getElementsByName("csrf-token")[0].content;

        let formData = new FormData();
        formData.append("authenticity_token", csrfToken);
        formData.append("state", state);
        formData.append("timestamp", new Date().toISOString());
        formData.append("error_message", error_message);

        fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/log_upload?upload_log_id=${upload_log_id}`, {
            method: "POST",
            headers: {
                "Accept": "application/json",
            },
            body: formData,
        });
    }

    uppyConfig() {
        return {
            autoProceed: true,
            onBeforeFileAdded: this.onBeforeFileAdded.bind(this),
        };
    }

    addFile(e) {
        const file = e.target.files[0];
        let replace;
        if (VIDEO_TYPES.includes(file.type)) {
            replace = this.getFileByType(VIDEO_TYPES);
        } else {
            replace = this.getFileByType(AUDIO_TYPES);
        }
        if (replace) this.uppy.removeFile(replace.id);
        this.uppy.addFile(file);
    }

    exit(e) {
        if (this.uploading.length > 0) {
            if (!window.confirm("Exiting now will cancel in progress uploads.\nAre you sure you want to close?")) {
                e.stopImmediatePropagation();
                return;
            }
        }

        window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}`);
    }

    async removeAudio(e) {
        const audio = this.getFileByType(["audio/mp3", "audio/mpeg", "audio/wav"]);
        if (audio) {
            this.uppy.removeFile(audio.id);
        }

        if (this.uploading.length == 0) {
            const csrfToken = document.getElementsByName("csrf-token")[0].content;

            let formData = new FormData();
            formData.append("authenticity_token", csrfToken);

            const payload = {
                method: "DELETE",
                headers: {
                    "Accept": "application/json",
                },
                body: formData
            };

            await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/remove_audio`, payload);
        }

        this.previewAudioTarget.parentElement.classList.add("hidden");
        this.audioOnlyTargets.forEach(t => t.classList.add("hidden"));
    }

    async removeVideo(e) {
        const video = this.getFileByType(VIDEO_TYPES);
        if (video) {
            this.uppy.removeFile(video.id);
        }

        if (this.uploading.length == 0) {
            const csrfToken = document.getElementsByName("csrf-token")[0].content;

            let formData = new FormData();
            formData.append("authenticity_token", csrfToken);

            const payload = {
                method: "DELETE",
                headers: {
                    "Accept": "application/json",
                },
                body: formData
            };

            await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/remove_video`, payload);
        }

        this.previewVideoTarget.parentElement.classList.add("hidden");
        this.videoOnlyTargets.forEach(t => t.classList.add("hidden"));
    }

    onBeforeFileAdded(file, files) {
        if (!ALLOWED_TYPES.includes(file.type)) {
            this.errorTarget.innerText = "Unsupported file type provided: " + file.type;
            return false
        }

        // Pull media duration for metadata.
        var objectURL = URL.createObjectURL(file.data);
        var audio = document.createElement('audio');
        audio.src = objectURL;
        audio.onloadedmetadata = () => {
            file.meta.duration = audio.duration
            URL.revokeObjectURL(objectURL)
        }

        return true;
    }

    async fileAdded(file) {
        if (this.hasWorkingTarget) {
            this.workingTarget.classList.remove("hidden");
        }

        const mediaType = file.type.split("/")[0];

        const url = new URL(window.location.origin + "/dashboard/channels/episodes/presigned_upload");

        url.searchParams.append("type", mediaType);
        url.searchParams.append("title", file.name);
        url.searchParams.append("episode", this.episodeIdValue);
        if (this.hasDynamicValue && this.hasTriggerEventValue) {
            url.searchParams.append("dynamic", this.dynamicValue);
            url.searchParams.append("trigger_event", this.triggerEventValue);
        }

        await fetch(url.toString())
            .then(response => response.json())
            .then(data => {
                const { episode_id, upload_log_id, ...bunny } = data;
                this.episodeIdValue = episode_id;
                file.meta.bunny = bunny;
                file.meta.upload_log_id = upload_log_id;

                this.uploading.push(upload_log_id);

                if (this.hasEventTarget) {
                    const event = new CustomEvent("upload-started", { detail: { episodeId: this.episodeIdValue } });
                    this.eventTarget.dispatchEvent(event);
                }

                if (VIDEO_TYPES.includes(file.type)) {
                    this.updateVideoPreview(file)
                } else {
                    this.updateAudioPreview(file);
                }

                this.logUploadState(file.meta.upload_log_id, "upload_started");
            })
            .finally(() => {
                if (this.hasWorkingTarget) {
                    this.workingTarget.classList.remove("hidden");
                }
            });
    }

    // This is for the publish button progress, not individual file progress
    progress(percentage) {
        if (this.hasShowUploadingTarget && percentage < 100) {
            this.showUploadingTargets.forEach(t => t.classList.remove("hidden"));
        }
        if (this.hasUploadPercentageTarget) {
            this.uploadPercentageTargets.forEach(t => t.innerText = `Uploading... ${percentage}%`);
        }
    }

    individualProgress(file, progress) {
        const percentage = Math.round(progress.bytesUploaded / progress.bytesTotal * 100);

        if (VIDEO_TYPES.includes(file.type)) {
            if (percentage < 100) {
                if (this.hasShowVideoUploadingTarget) {
                    this.showVideoUploadingTargets.forEach(t => t.classList.remove("hidden"));
                }
                if (this.hasShowVideoUploadedTarget) {
                    this.showVideoUploadedTargets.forEach(t => t.classList.add("hidden"));
                }
            }
            if (this.hasVideoUploadPercentageTarget) {
                this.videoUploadPercentageTargets.forEach(t => t.innerText = `Uploading... ${percentage}%`);
            }
            // compute and render speed/ETA
            const now = Date.now();
            const stats = this._getOrInitStats(file.id, now);
            const dt = (now - stats.lastTime) / 1000;
            const minSampleS = 0.5;
            if (dt >= minSampleS) {
                const db = progress.bytesUploaded - stats.lastBytes;
                const inst = db / dt;
                const ema = this._emaBps(stats.speedBps, inst); // moving average
                stats.speedBps = ema;
                stats.lastTime = now;
                stats.lastBytes = progress.bytesUploaded;
            }
            const elapsedS = (now - stats.startedAt) / 1000;
            const avgBps = this._avgBps(progress.bytesUploaded, elapsedS);
            const hybridBps = this._hybridBps(stats.speedBps, avgBps);
            // hide stats if less than 3 seconds or 5MB uploaded
            const warmedUp = this._shouldShowStats(elapsedS, progress.bytesUploaded);
            let statsHtml = "";
            if (warmedUp && hybridBps > 0) {
                const remainingBytes = Math.max(progress.bytesTotal - progress.bytesUploaded, 0);
                const secondsRemaining = remainingBytes / hybridBps;
                const speedText = `${this._formatBytes(hybridBps)}/s`;
                const etaText = `about ${this._formatDuration(secondsRemaining)} left`;
                statsHtml = `${speedText}, ${etaText}`;
            }
            this._updateVideoUploadStats(statsHtml);
        } else {
            if (percentage < 100) {
                if (this.hasShowAudioUploadingTarget) {
                    this.showAudioUploadingTargets.forEach(t => t.classList.remove("hidden"));
                }
                if (this.hasShowAudioUploadedTarget) {
                    this.showAudioUploadedTargets.forEach(t => t.classList.add("hidden"));
                }
            }
            if (this.hasAudioUploadPercentageTarget) {
                this.audioUploadPercentageTargets.forEach(t => t.innerText = `Uploading... ${percentage}%`);
            }
        }

        if (percentage == 100) {
            this.logUploadState(file.meta.upload_log_id, "processing_started");
            delete this._uploadStats[file.id];
            // hide stats once upload completes
            if (VIDEO_TYPES.includes(file.type)) {
                if (this.hasVideoUploadStatsTarget) {
                    this.videoUploadStatsTargets.forEach(t => { t.innerText = ""; t.classList.add("hidden"); });
                }
            }
        }
    }

    async commitUpload(file, _) {
        let csrfToken = document.getElementsByName("csrf-token")[0].content;

        let formData = new FormData();
        formData.append("authenticity_token", csrfToken);

        const metadata = {
            size: file.size,
            filename: file.name,
            mime_type: file.type,
            duration: file.meta.duration,
        };
        if (VIDEO_TYPES.includes(file.type)) {
            formData.append("episode[podcast_video]", file.meta.bunny.video_id);
            formData.append("episode[podcast_video_meta]", JSON.stringify(metadata));
        } else {
            formData.append("episode[podcast_audio]", file.meta.bunny.video_id);
            formData.append("episode[podcast_audio_meta]", JSON.stringify(metadata));
        }
        formData.append("upload_log_id", file.meta.upload_log_id);

        const payload = {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
            },
            body: formData,
        };

        let attempts = 0;
        let response;
        while (attempts < 3) {
            response = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}`, payload);
            if (response.ok) {
                if (AUDIO_TYPES.includes(file.type)) {
                    this.showAudioErrorTargets.forEach(t => t.classList.add("hidden"));
                } else {
                    this.showVideoErrorTargets.forEach(t => t.classList.add("hidden"));
                }
                break; // Exit the loop if the request was successful
            }
            attempts++;
        }

        if (!response.ok) {
            this.logUploadState(file.meta.upload_log_id, "error", `Commit failed with status ${response.status}`);
            this.retryUpload(file, null, null);
            return;
        }

        // Show uploaded state
        this.showVideoUploadingTargets.forEach(t => t.classList.add("hidden"));
        this.showVideoUploadedTargets.forEach(t => t.classList.remove("hidden"));
        this.showAudioUploadingTargets.forEach(t => t.classList.add("hidden"));
        this.showAudioUploadedTargets.forEach(t => t.classList.remove("hidden"));

        this.uploading.splice(this.uploading.indexOf(file.meta.upload_log_id), 1);
        if (this.uploading.length == 0) {
            this.showUploadingTargets.forEach(t => t.classList.add("hidden"));
            this.showUploadedTargets.forEach(t => t.classList.remove("hidden"));
        }

        // If a video was uploaded and audio extraction feature is enabled, start polling status
        if (VIDEO_TYPES.includes(file.type)) {
            // Mark extracting now; controller endpoint will have queued the job on update
            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        }
    }

    retryUpload(file, error, response) {
        if (AUDIO_TYPES.includes(file.type)) {
            this.showAudioErrorTargets.forEach(t => t.classList.remove("hidden"));
        } else {
            this.showVideoErrorTargets.forEach(t => t.classList.remove("hidden"));
        }

        if (error) {
            this.logUploadState(file.meta.upload_log_id, "error", error.message);
        }

        // Automatically retry uploads on failure. Upload failures are usually intermittent.
        this.uppy.retryUpload(file.id);
    }

    attachTus(uppy) {
        uppy.use(window.Uppy.Tus, {
            endpoint: "https://video.bunnycdn.com/tusupload",
            async onBeforeRequest(req, file) {
                var data = file.meta.bunny

                req.setHeader('AuthorizationSignature', data.signature);
                req.setHeader('AuthorizationExpire', data.expire);
                req.setHeader('VideoId', data.video_id);
                req.setHeader('LibraryId', data.library_id);
                // Custom correlation headers for provider diagnostics
                try {
                    if (file?.meta?.upload_log_id) req.setHeader('X-Upload-Trace-Id', file.meta.upload_log_id);
                } catch (_) {}
            },
        });

        uppy.on('file-added', this.fileAdded.bind(this))
            .on('progress', this.progress.bind(this))
            .on('upload-progress', this.individualProgress.bind(this))
            .on('upload-success', this.commitUpload.bind(this))
            .on('upload-error', this.retryUpload.bind(this))
    }

    // Extraction helpers

    onExtractionComplete() {
        if (this.hasAddAudioTarget) {
            this.addAudioTarget.classList.add("hidden");
        }
        if (this.hasPreviewAudioTarget) {
            this.previewAudioTarget.parentElement.classList.remove("hidden");
        }
    }

    async checkExtractionStatusAndMaybePoll() {

        try {
            const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
            if (!res.ok) return;
            const data = await res.json();
            const status = data.status;
            if (["queued", "processing"].includes(status)) {
                this.extracting = true;
                this.toggleExtractionNotice();
                this.startExtractionPolling();
            } else {
                this.extracting = false;
                this.toggleExtractionNotice();
                if (status === "done") {
                    this.onExtractionComplete();
                }
            }
        } catch (e) { /* noop */ }
    }

    startExtractionPolling() {
        if (this._extractionPollTimer) return; // already polling
        const poll = async () => {
            try {
                const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
                if (!res.ok) return;
                const { status } = await res.json();
                if (["queued", "processing"].includes(status)) {
                    this.extracting = true;
                    this.toggleExtractionNotice();
                } else {
                    this.extracting = false;
                    this.toggleExtractionNotice();
                    if (this.hasAudioExtractionHintTarget) {
                        this.audioExtractionHintTarget.classList.add("hidden");
                    }
                    clearInterval(this._extractionPollTimer);
                    this._extractionPollTimer = null;
                    if (status === "done") {
                        this.onExtractionComplete();
                    }
                    try {
                        if (window.Turbo && this.hasEventTarget) {
                            window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}/detail`, { frame: "step" });
                        }
                    } catch (_) {}
                }
            } catch (e) { /* noop */ }
        };
        poll();
        this._extractionPollTimer = setInterval(poll, 4000);
    }

    async retryExtraction() {
        const hadRetryTarget = this.hasRetryControlTarget;
        if (hadRetryTarget) {
            this.retryControlTarget.classList.add("hidden");
        }

        try {
            const csrfToken = document.getElementsByName("csrf-token")[0].content;
            let formData = new FormData();
            formData.append("authenticity_token", csrfToken);

            const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/retry_audio_extraction`, {
                method: "POST",
                headers: { "Accept": "application/json" },
                body: formData
            });
            if (!res.ok) {
                if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
                return;
            }

            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        } catch (_) {
            if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
        }
    }

    async cancelExtractionOrRemoveAudio() {
        // If currently extracting, cancel; otherwise, remove audio if present
        if (this.extracting) {
            try {
                const csrfToken = document.getElementsByName("csrf-token")[0].content;
                let formData = new FormData();
                formData.append("authenticity_token", csrfToken);
                await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/cancel_audio_extraction`, { method: "POST", body: formData, headers: { "Accept": "application/json" } });
            } finally {
                this.extracting = false;
                this.toggleExtractionNotice();
                // Immediately hide the extraction hint on cancel click
                if (this.hasAudioExtractionHintTarget) {
                    this.audioExtractionHintTarget.classList.add("hidden");
                }
                if (this._extractionPollTimer) { clearInterval(this._extractionPollTimer); this._extractionPollTimer = null; }
            }
        } else {
            // Fall back to removing audio via existing action
            await this.removeAudio();
        }
    }

    _getOrInitStats(fileId, now) {
        if (!this._uploadStats[fileId]) {
            this._uploadStats[fileId] = { startedAt: now, lastTime: now, lastBytes: 0, speedBps: 0 };
        }
        return this._uploadStats[fileId];
    }

    _emaBps(prev, inst) {
        return prev ? (prev * 0.7 + inst * 0.3) : inst;
    }

    _avgBps(bytesUploaded, elapsedS) {
        return elapsedS > 0 ? (bytesUploaded / elapsedS) : 0;
    }

    _hybridBps(speedBps, avgBps) {
        return (speedBps && avgBps) ? (0.5 * speedBps + 0.5 * avgBps) : (speedBps || avgBps);
    }

    _shouldShowStats(elapsedS, bytesUploaded) {
        return elapsedS >= 3 && bytesUploaded >= 5 * 1024 * 1024;
    }

    _updateVideoUploadStats(statsHtml) {
        if (this.hasVideoUploadStatsTarget) {
            this.videoUploadStatsTargets.forEach(t => {
                t.innerHTML = statsHtml;
                if (statsHtml) {
                    t.classList.remove("hidden");
                } else {
                    t.classList.add("hidden");
                }
            });
        }
    }

    _formatBytes(bytes) {
        if (!isFinite(bytes) || bytes <= 0) return "0 B";
        const units = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
        const value = bytes / Math.pow(1024, i);
        return `${value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${units[i]}`;
    }

    _formatDuration(totalSeconds) {
        const s = Math.max(0, Math.round(totalSeconds));
        const hours = Math.floor(s / 3600);
        const minutes = Math.floor((s % 3600) / 60);
        const seconds = s % 60;
        if (hours > 0) {
            return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        }
        return `${minutes}:${String(seconds).padStart(2, "0")}`;
    }
}

UploadController.targets = [
    "event", "dropZone", "working", "error", "previewVideo", "previewAudio",
    "videoImageInput", "audioImageInput",
    "showUploading", "showUploaded", "uploadPercentage",
    "showAudioUploading", "showAudioUploaded", "audioUploadPercentage", "showAudioError",
    "showVideoUploading", "showVideoUploaded", "videoUploadPercentage", "showVideoError", "videoUploadStats",
    "extractionNotice", "videoOnly", "audioOnly", "retryControl", "addAudio", "audioExtractionHint"
];
UploadController.values = { episodeId: Number, worker: String, dynamic: Boolean, triggerEvent: String };
